/**
 * Fee Models service — the fee-config domain (admin-managed). Owns three Airtable tables:
 *   Fee Models       tbln43xvqb8QTW0Z9  — the knobs that drive proposal fees
 *   Hourly Fee Rates tbl9jejFwE7WrUhpz  — the staff rate card (per position/year)
 *   Practice Positions tbleWeJskivhCnJ2j — the roles the rate card prices
 *
 * Fee math stays in Airtable. This editor writes the *settings*; Airtable recomputes every linked
 * proposal. Only editable (non-formula) fields are exposed — the 11-bracket fee curve cascades from
 * Bracket 1 Budget + Bracket 1 Rate + Fee Decay Factor, so we expose only those three.
 *
 * Percent fields are stored as decimals in Airtable (0.2 = 20%, 1.2 = 120%). FEE_MODEL_GROUPS marks
 * each field's type ('pct' | 'cur' | 'num' | 'text'); the editor converts pct ×100 for display and
 * ÷100 on write.
 */
import { apiGet, apiGetOne, apiGetAll, apiPatch, apiPost } from './_api.js'

const TBL_FM = 'tbln43xvqb8QTW0Z9'
const TBL_HR = 'tbl9jejFwE7WrUhpz'

/* ── Editable field groups (the editor's schema) ─────────────────────────── */
export const FEE_MODEL_GROUPS = [
  { title: 'Rating weights', hint: 'Fee-% delta per rating point (Complexity/Portfolio/Client).', fields: [
    { key: 'Complexity Adjustment Weight', label: 'Complexity', type: 'pct' },
    { key: 'Portfolio Adjustment Weight', label: 'Portfolio', type: 'pct' },
    { key: 'Client Adjustment Weight', label: 'Client', type: 'pct' },
  ] },
  { title: 'Phase proportions', hint: 'Share of fee per phase. PD+SD+DD+CD+PR+AD must total 100%; DA + IA are add-ons.', validateSum: ['PD Proportion', 'SD Proportion', 'DD Proportion', 'CD Proportion', 'PR Proportion', 'AD Proportion'], fields: [
    { key: 'PD Proportion', label: 'Pre-design (PD)', type: 'pct' },
    { key: 'SD Proportion', label: 'Schematic (SD)', type: 'pct' },
    { key: 'DD Proportion', label: 'Design Dev (DD)', type: 'pct' },
    { key: 'CD Proportion', label: 'Construction Docs (CD)', type: 'pct' },
    { key: 'PR Proportion', label: 'Permitting (PR)', type: 'pct' },
    { key: 'AD Proportion', label: 'Arch Detailing (AD)', type: 'pct' },
    { key: 'DA Proportion', label: 'Discretionary Approvals (DA, add-on)', type: 'pct' },
    { key: 'IA Proportion', label: 'Interior Architecture (IA, add-on)', type: 'pct' },
  ] },
  { title: 'Phase average billing rates', hint: 'Blended $/hr per phase (drives hours from fee).', fields: [
    { key: 'PD Average Billing Rate', label: 'PD', type: 'cur' },
    { key: 'SD Average Billing Rate', label: 'SD', type: 'cur' },
    { key: 'DA Average Billing Rate', label: 'DA', type: 'cur' },
    { key: 'DD Average Billing Rate', label: 'DD', type: 'cur' },
    { key: 'CD Average Billing Rate', label: 'CD', type: 'cur' },
    { key: 'PR Average Billing Rate', label: 'PR', type: 'cur' },
    { key: 'AD Average Billing Rate', label: 'AD', type: 'cur' },
    { key: 'IA Average Billing Rate', label: 'IA', type: 'cur' },
    { key: 'BD Average Billing Rate', label: 'BD', type: 'cur' },
    { key: 'CS Average Billing Rate', label: 'CS', type: 'cur' },
  ] },
  { title: 'Project-type adjustments', hint: 'Fee multiplier by project type (1.0 = no change).', fields: [
    { key: 'Standard Residential Adjustment', label: 'Standard Residential', type: 'pct' },
    { key: 'Luxury Residential Adjustment', label: 'Luxury Residential', type: 'pct' },
    { key: 'Standard Commercial Adjustment', label: 'Standard Commercial', type: 'pct' },
    { key: 'Basic Commercial Adjustment', label: 'Basic Commercial', type: 'pct' },
    { key: 'Luxury Commercial Adjustment', label: 'Luxury Commercial', type: 'pct' },
  ] },
  { title: 'Scope factors', hint: 'Multipliers for special conditions.', fields: [
    { key: 'Specialty Area Factor', label: 'Specialty area', type: 'pct' },
    { key: 'Moderate Alteration Factor', label: 'Moderate alteration', type: 'pct' },
    { key: 'Light Alteration Factor', label: 'Light alteration', type: 'pct' },
    { key: 'Bad Geology Factor', label: 'Bad geology', type: 'pct' },
  ] },
  { title: 'Fee curve & FPV bounds', hint: 'Brackets 2–11 cascade from Bracket 1 + decay. FPV is clamped between floor and ceiling.', fields: [
    { key: 'Bracket 1 Budget', label: 'Bracket 1 budget', type: 'cur' },
    { key: 'Bracket 1 Rate', label: 'Bracket 1 rate (%)', type: 'num' },
    { key: 'Fee Decay Factor', label: 'Fee decay factor', type: 'num' },
    { key: 'Floor FPV', label: 'Floor FPV', type: 'num' },
    { key: 'Ceiling FPV', label: 'Ceiling FPV', type: 'num' },
    { key: 'High Percent', label: 'High percent', type: 'pct' },
    { key: 'Low Percent', label: 'Low percent', type: 'pct' },
  ] },
  { title: 'Discounts & splits', fields: [
    { key: 'Budget Reduction', label: 'Budget reduction', type: 'pct' },
    { key: 'Friends and Family Discount', label: 'Industry Partner discount', type: 'pct' },
    { key: 'AE Split', label: 'AE split', type: 'pct' },
  ] },
  { title: 'As-built model', hint: 'Per-SF rate for existing-building 3D model.', fields: [
    { key: 'As-built Price Per SF with Field Survey', label: 'With field survey', type: 'cur' },
    { key: 'As-built Price Per SF without Field Survey', label: 'Without field survey', type: 'cur' },
  ] },
  { title: 'Travel & meetings', fields: [
    { key: 'Travel Per Diem', label: 'Travel per diem', type: 'cur' },
    { key: 'Average Hours Per Meeting', label: 'Avg hours / meeting', type: 'num' },
    { key: 'Regional Meeting Factor', label: 'Regional meeting factor', type: 'num' },
  ] },
  { title: 'Value Indicator (VI) per-SF rates', hint: 'Drives Factored Project Value from areas.', fields: [
    { key: 'VI - New Occupiable', label: 'New occupiable', type: 'cur' },
    { key: 'VI - Alter Occupiable', label: 'Alter occupiable', type: 'cur' },
    { key: 'VI - New Garage', label: 'New garage', type: 'cur' },
    { key: 'VI - Alter Garage', label: 'Alter garage', type: 'cur' },
    { key: 'VI - New Storage Utility', label: 'New storage/utility', type: 'cur' },
    { key: 'VI - Alter Storage Utility', label: 'Alter storage/utility', type: 'cur' },
    { key: 'VI - New Elevated Deck', label: 'New elevated deck', type: 'cur' },
    { key: 'VI - Alter Elevated Deck', label: 'Alter elevated deck', type: 'cur' },
    { key: 'VI - New Covered Patio', label: 'New covered patio', type: 'cur' },
    { key: 'VI - Alter Covered Patio', label: 'Alter covered patio', type: 'cur' },
    { key: 'VI - Alter Exterior Wall', label: 'Alter exterior wall', type: 'cur' },
    { key: 'VI - Alter Roofing', label: 'Alter roofing', type: 'cur' },
    { key: 'VI - Hardscape', label: 'Hardscape', type: 'cur' },
    { key: 'VI - Pool Spa', label: 'Pool/spa area', type: 'cur' },
    { key: 'VI - Pool', label: 'Pool (each)', type: 'cur' },
    { key: 'VI - Spa', label: 'Spa (each)', type: 'cur' },
    { key: 'VI -Site Retaining Wall', label: 'Site retaining wall', type: 'cur' },
  ] },
]

// Flat list of every editable Fee Model field key (for clone).
export const FEE_MODEL_FIELD_KEYS = FEE_MODEL_GROUPS.flatMap(g => g.fields.map(f => f.key))

/* ── Fee Models ──────────────────────────────────────────────────────────── */
export async function getFeeModels() {
  const recs = await apiGetAll(TBL_FM)
  return recs.map(r => ({
    id: r.id,
    name: r.fields['Name'] || '(unnamed)',
    proposalCount: r.fields['Number of Proposals'] || 0,
    basicTotal: r.fields['Basic Phases Total %'],
    basicOk: r.fields['Basic Phases Total % Message'] === 'OKAY',
  })).sort((a, b) => String(b.name).localeCompare(String(a.name)))
}

export async function getFeeModel(id) {
  const r = await apiGetOne(TBL_FM, id)
  return r || null
}

export function updateFeeModel(id, fields) {
  return apiPatch(TBL_FM, id, fields, { typecast: true })
}

/* Airtable writes return { error } on failure (the proxy passes the body through verbatim)
   instead of throwing — surface it so a bad write shows up as a real error instead of
   silently doing nothing. */
function throwOnError(res, what) {
  if (res && res.error) {
    const msg = (res.error && (res.error.message || res.error.type)) || ('Could not save the ' + what)
    throw new Error(msg)
  }
  return res
}

export async function createFeeModel(fields) {
  const res = throwOnError(await apiPost(TBL_FM, fields, { typecast: true }), 'fee model')
  return res && res.id ? res : null
}

/** Clone a model's editable settings into a new model (name → "<name> (copy)"). Rate card not copied. */
export async function cloneFeeModel(id, newName) {
  const src = await getFeeModel(id)
  if (!src) return null
  const fields = { Name: newName || `${src.fields['Name'] || 'Fee Model'} (copy)` }
  for (const k of FEE_MODEL_FIELD_KEYS) if (src.fields[k] != null) fields[k] = src.fields[k]
  return createFeeModel(fields)
}

/* ── Rate card (the model's linked Hourly Fee Rates) ─────────────────────── */
export async function getRateCard(feeModelId) {
  const model = await apiGetOne(TBL_FM, feeModelId)
  const ids = model && Array.isArray(model.fields['Hourly Positions']) ? model.fields['Hourly Positions'] : []
  if (!ids.length) return []
  const out = []
  for (let i = 0; i < ids.length; i += 50) {
    const chunk = ids.slice(i, i + 50)
    const formula = 'OR(' + chunk.map(x => `RECORD_ID()='${x}'`).join(',') + ')'
    const recs = await apiGet(TBL_HR, 'filterByFormula=' + encodeURIComponent(formula))
    out.push(...recs.map(r => ({
      id: r.id,
      position: (Array.isArray(r.fields['Position']) ? null : null) || r.fields['Name'] || '',
      year: r.fields['Year'] || '',
      rate: r.fields['Rate'] || 0,
      contractorRate: r.fields['Contractor Rate'] || 0,
      targetUtilization: r.fields['Target Utilization'] || 0,
      contribution: r.fields['Contribution to Ave Billing Rate'] || 0,
      sortOrder: r.fields['Sort Order'] || 0,
    })))
  }
  return out.sort((a, b) => (a.sortOrder - b.sortOrder) || String(a.position).localeCompare(String(b.position)))
}

export function updateRateRow(id, { rate, contractorRate, targetUtilization }) {
  const f = {}
  if (rate !== undefined) f['Rate'] = rate
  if (contractorRate !== undefined) f['Contractor Rate'] = contractorRate
  if (targetUtilization !== undefined) f['Target Utilization'] = targetUtilization
  return apiPatch(TBL_HR, id, f, { typecast: true })
}
